import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoText from '@/components/atoms/InfoText';
import TitleText from '@/components/atoms/TitleText';

interface PaymentSummaryData {
  subtotal: number;
  vat: number;
  vatPercentage: number;
  shipping?: string | number;
  commission: number;
  totalEst: number;
  currency: string;
}

interface FeedbackPaymentSummaryProps {
  payment: PaymentSummaryData;
  'data-testid'?: string;
}

export const FeedbackPaymentSummary: React.FC<FeedbackPaymentSummaryProps> = ({
  payment,
  'data-testid': dataTestId = 'store-order-payment-summary',
}) => {
  const { t } = useTranslation();

  if (!payment || (!payment.subtotal && !payment.totalEst)) return null;

  return (
    <div
      className="p-5 border rounded-xl bg-light-surface-primary dark:bg-dark-surface-primary border-light-input-border dark:border-dark-input-border space-y-4"
      data-testid={dataTestId}
    >
      <TitleText
        text={t('logisticsFeedback.paymentSummaryTitle')}
        size="base"
        className="!font-bold !text-light-primary dark:!text-dark-primary"
        data-testid={`${dataTestId}-title`}
      />

      <div className="space-y-1" data-testid={`${dataTestId}-items`}>
        {/* Subtotal (Est.) */}
        <div className="flex items-center justify-between p-2 rounded-[4px] gap-4" data-testid={`${dataTestId}-subtotal`}>
          <InfoText
            text={t('logisticsFeedback.subtotalEst')}
            className="!text-sm !font-normal !leading-none !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-subtotal-label`}
          />
          <div className="flex items-center gap-1">
            <InfoText
              text={payment.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              className="!text-sm !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-subtotal-value`}
            />
            <InfoText
              text={payment.currency || t('SAR')}
              className="!text-xs !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-subtotal-currency`}
            />
          </div>
        </div>

        {/* VAT */}
        <div className="flex items-center justify-between p-2 rounded-[4px] gap-4" data-testid={`${dataTestId}-vat`}>
          <InfoText
            text={`${t('logisticsFeedback.vat')}${payment.vatPercentage ? ` (${payment.vatPercentage}%)` : ''}`}
            className="!text-sm !font-normal !leading-none !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-vat-label`}
          />
          <div className="flex items-center gap-1">
            <InfoText
              text={payment.vat.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              className="!text-sm !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-vat-value`}
            />
            <InfoText
              text={payment.currency || t('SAR')}
              className="!text-xs !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-vat-currency`}
            />
          </div>
        </div>

        {/* Shipping */}
        <div className="flex items-center justify-between p-2 rounded-[4px] gap-4" data-testid={`${dataTestId}-shipping`}>
          <InfoText
            text={t('logisticsFeedback.shippingToQuoted')}
            className="!text-sm !font-normal !leading-none !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-shipping-label`}
          />
          <InfoText
            text={payment.shipping ? String(payment.shipping) : '--'}
            className="!text-sm !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-shipping-value`}
          />
        </div>

        {/* Commutation */}
        <div className="flex items-center justify-between p-2 rounded-[4px] gap-4" data-testid={`${dataTestId}-commission`}>
          <InfoText
            text={t('logisticsFeedback.commutation')}
            className="!text-sm !font-normal !leading-none !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-commission-label`}
          />
          <div className="flex items-center gap-1">
            <InfoText
              text={payment.commission.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              className="!text-sm !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-commission-value`}
            />
            <InfoText
              text={payment.currency || t('SAR')}
              className="!text-xs !font-bold !leading-none !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-commission-currency`}
            />
          </div>
        </div>

        <hr className="border-light-input-border dark:border-dark-input-border my-2" />

        {/* Total Est. */}
        <div className="flex items-center justify-between p-2 rounded-[4px] gap-4" data-testid={`${dataTestId}-total`}>
          <TitleText
            text={t('logisticsFeedback.totalEst')}
            size="base"
            className="!font-bold !text-light-primary dark:!text-dark-primary"
            data-testid={`${dataTestId}-total-label`}
          />
          <div className="flex items-center gap-1">
            <InfoText
              text={payment.totalEst.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              className="!text-base !font-bold !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-total-value`}
            />
            <InfoText
              text={payment.currency || t('SAR')}
              className="!text-xs !font-bold !text-light-primary dark:!text-dark-primary"
              data-testid={`${dataTestId}-total-currency`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

